/* eslint-disable @typescript-eslint/no-misused-promises */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import PromoCodeIcon from '@mui/icons-material/LocalOfferOutlined'
import CheckIcon from '@mui/icons-material/Check'
import RemoveIcon from '@mui/icons-material/CancelOutlined'

import { getPromoCode, useCurrentMove } from '../../../modules/moves'
import { useAppConfig } from '../../../modules/appConfig'
import Form, { Input, SubmitButton } from '../../Form'
import { InputSize } from '../../Form/controls/Input'
import Button, { ButtonSize, ButtonVariant } from '../../Button'
import Tooltip from '../../Tooltip'
import Spinner from '../../Spinner'
import useValidationSchema from './validationSchema'

type PromoCodeFormProps = {
  onSubmit: (promoCode: string | null) => Promise<void> | void
}

const PromoCodeForm: React.FC<PromoCodeFormProps> = ({
  onSubmit,
}) => {
  const { t } = useTranslation()
  const schema = useValidationSchema()
  const move = useCurrentMove()
  const form = useForm({
    resolver: yupResolver(schema),
  })
  const promoCode = getPromoCode(move)
  const [visible, setVisible] = useState(!!promoCode)
  const [isRemovingPromoCode, setIsRemovingPromoCode] = useState(false)
  const { data: appConfig } = useAppConfig()
  const promoCodeEditable = appConfig?.platform?.promoCode?.editableByCustomer ?? true
  const protectedPromoCode = !!promoCode?.protected
  const canEditPromoCode = promoCodeEditable && !protectedPromoCode

  if (!visible && !canEditPromoCode) {
    return null
  }

  if (!visible) {
    return (
      <div className="pb-4 pl-2">
        <Button
          onClick={() => { setVisible(true) }}
          variant={ButtonVariant.Transparent}
          className="!mt-0 !p-0 !text-primary !underline"
        >
          { t('forms.promoCode.haveACoupon') }
        </Button>
      </div>
    )
  }

  const removePromoCode = async (event: React.MouseEvent) => {
    event.stopPropagation()
    try {
      setIsRemovingPromoCode(true)
      setVisible(false)
      await onSubmit(null)
    } finally {
      setIsRemovingPromoCode(false)
    }
  }

  return (
    <div className="my-4 flex flex-col justify-between gap-y-2 rounded-md border bg-white p-4 sm:flex-row lg:p-8">
      <Form
        form={form}
        formProps={{
          className: 'w-full',
        }}
        onSubmit={async (data: { promoCode: string }) => { await onSubmit(data.promoCode.trim()) }}
      >

        <div className="flex flex-col justify-between gap-y-2 sm:flex-row">
          <div className="flex items-center gap-3 font-sans text-neutral-500">
            <PromoCodeIcon className="!text-[18px]" />
            { t('forms.promoCode.coupon') }
          </div>

          { promoCode
            ? (
              <div className="flex items-center gap-x-2">
                <div className="text-green-700">
                  <CheckIcon className="!text-[16px]" />
                  { ' ' }
                  { promoCode.code }
                </div>

                { isRemovingPromoCode && <Spinner small /> }

                { canEditPromoCode && !isRemovingPromoCode && (
                  <Tooltip tooltip={t('forms.promoCode.removeCoupon')}>
                    <div
                      className="cursor-pointer text-red-800"
                      onClick={removePromoCode}
                    >
                      <RemoveIcon className="!mt-[-3px] !text-[20px]" />
                    </div>
                  </Tooltip>
                ) }
              </div>
              )
            : (
              <div className="flex sm:w-[350px]">
                <Input
                  name="promoCode"
                  size={InputSize.Small}
                  className="child:!rounded-r-none"
                />
                <div>
                  <SubmitButton
                    size={ButtonSize.Small}
                    className="!h-[40px] !rounded-l-none !rounded-r-[4px] !border-l-0 !bg-neutral-400 !shadow-none"
                  >
                    { t('actions.apply') }
                  </SubmitButton>
                </div>
              </div>
              ) }

        </div>
      </Form>
    </div>
  )
}

export default PromoCodeForm
